﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// The default login page. This page handles login through form username/password, loading of credentials
// from existing authentication token and loading of credentials from server side session (used by most
// SSO solutions).

// Can be tested for all customers. It's the default page when not yet authenticated.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import classnames from "classnames";
import { IntlContext, useIntl } from "~base/Internationalization/IntlContext";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { useSettings } from "~base/Settings/SettingsContext";
import {
    ConsilioApiToken,
    ServerSessionState,
    TokenRequestMode,
    TokenRequestStatus,
    useAuthToken,
} from "~base/Services/API/AuthToken";
import { TitleText } from "~base/Components/Primitives/TitleText";
import { Button } from "~base/Components/Primitives/Button";
import Grid from "~base/Components/Layout/Grid";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import { RouteLink } from "~base/Components/Primitives/Link";
import {
    getSpacingClasses,
    useJssSpacingStyles,
} from "~base/Components/Layout/JssSpacing";
import { horizontalCenterPadding } from "~base/Components/Primitives/TitleContainer";
import PoweredByConsilio from "~base/Components/Composite/PoweredByConsilio";
import IconButton from "~base/Components/Primitives/IconButton";
import { LoginPageCustomizations } from "./Customization";
import Form from "~base/Components/Primitives/Form";
import { SplashPage } from "~base/Components/Pages/Shared/SplashPage";
import { LoginPageState, reducer } from "./Store";
import TitlePassword from "~base/Components/Primitives/TitlePassword";
import { useStyles } from "./Styles";
import { SplashContent } from "~base/Components/Pages/Shared/SplashContent";
import { Logo } from "~base/Components/Composite/Logo";
import {
    getSameSiteSafeUrlParam,
    isSameSiteUrl,
    useNavigateService,
} from "~base/Services/NavigateService";
import TextBlock from "~base/Components/Primitives/TextBlock";
import log from "loglevel";
import { useGetLoginInfo } from "~base/Services/API/Account/GetLoginInfo";
import { getErrorMessage } from "~base/Services/API";
import { ExternalLoginProvider } from "./ChildComponents/ExternalLoginProvider";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type LoginProps = {
    customizations: LoginPageCustomizations;
};

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const LoginPage: React.FunctionComponent<LoginProps> = (
    props: LoginProps
) => {
    const intl = useIntl();
    const navigate = useNavigateService();
    const [searchParams] = useSearchParams();
    const settings = useSettings();
    const serverVirtualPath = settings.valueAsString("serverVirtualPath");
    const tenantName = settings.valueAsString("detectedTenantName");
    const usernameFromQs = searchParams.get("username");
    const forms = searchParams.get("forms");

    const sessionless = settings.valueAsBool("Sessionless");

    const paramReturnUrl = getSameSiteSafeUrlParam(searchParams, "ReturnUrl");

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("LoginPage"),
        reducer,
        {
            username: usernameFromQs ?? "",
            password: "",
            showLoginPanel: false,
            tryResolveFromAppStorage: paramReturnUrl !== null,
            tryResolveFromCookie: !sessionless && paramReturnUrl !== null,
            clearToken: !paramReturnUrl,
            tryFormLogin: false,
            errorMessage: null,
            isLoading: false,
            requestServerSession: false,
            navigateUri: null,
        }
    );

    const callLoginInfo =
        props.customizations.queryLoginInfo &&
        !state.errorMessage &&
        !state.loginInfo;
    const [loadingProviders, ___] = useGetLoginInfo({
        tenantName: tenantName,
        call: callLoginInfo,
        anonymous: true,
        throwError: false,
        callback: async (status, response, responseData) => {
            if (status == "Success") {
                dispatch({ type: "setLoginInfo", data: responseData });
            } else {
                dispatch({
                    type: "fetchError",
                    message: await getErrorMessage(intl, response),
                });
            }
        },
    });

    const [tokenLoading, token, apiTokenState, serverSessionState] =
        useAuthToken({
            clearToken: state.clearToken,
            resolveFromAppStorage: state.tryResolveFromAppStorage,
            resolveFromCookie: state.tryResolveFromCookie,
            resolveFromFormCredentials: state.tryFormLogin,
            requestServerSession: state.requestServerSession,
            setSessionCookie: !sessionless,
            username: state.username,
            password: state.password,
            tenantName: tenantName,
            callback: (
                status: TokenRequestStatus,
                mode: TokenRequestMode,
                _: ConsilioApiToken
            ) => {
                log.info(`Token callback - status: ${status} - mode: ${mode}`);
                const redirectUrl = tokenLoadedRedirectUrl(
                    paramReturnUrl,
                    serverVirtualPath,
                    sessionless,
                    status,
                    state,
                    serverSessionState,
                    tenantName
                );

                const errorMessage = getLoginFailedMessage(intl, status, mode);

                dispatch({
                    type: "completedLoginAttempt",
                    errorMessage: errorMessage,
                    redirectUri: redirectUrl,
                    requestMode: mode,
                    status: status,
                });
            },
        });

    useEffect(() => {
        if (token) {
            const returnUrl =
                paramReturnUrl ?? `${serverVirtualPath}Home/Start`;
            if (returnUrl) {
                const completedLoadingAuthentication =
                    apiTokenState != "Unknown" &&
                    serverSessionState != "Unknown";
                const refreshedAuthentication =
                    apiTokenState != "ApplicationStorage" ||
                    serverSessionState != "ExistingSession";

                if (
                    completedLoadingAuthentication &&
                    refreshedAuthentication &&
                    !state.isNavigating
                ) {
                    dispatch({ type: "navigate", uri: returnUrl });
                } else if (
                    completedLoadingAuthentication &&
                    !refreshedAuthentication &&
                    !state.isNavigating
                ) {
                    const referrer = document.referrer;
                    const qsIndex = referrer.indexOf("?");
                    const referrerQs =
                        qsIndex != -1 ? referrer.substr(qsIndex) : null;
                    const params = new URLSearchParams(referrerQs);
                    const referrerReturnUrl = getSameSiteSafeUrlParam(
                        params,
                        "ReturnUrl"
                    );

                    if (referrerReturnUrl != returnUrl) {
                        dispatch({ type: "navigate", uri: returnUrl });
                    } else {
                        // We're probably being sent back and forth due to unauthorized (should be forbidden?).
                        // Block the request and show Unauthorized page.
                        dispatch({
                            type: "navigate",
                            uri: `${serverVirtualPath}Account/Unauthorized`,
                        });
                    }
                } else if (
                    !state.isLoading &&
                    !state.requestServerSession &&
                    serverSessionState == "Unknown" &&
                    !sessionless
                ) {
                    dispatch({ type: "requestServerSession" });
                }
            }
        }
    }, [
        paramReturnUrl,
        token,
        serverVirtualPath,
        dispatch,
        serverSessionState,
        apiTokenState,
        state.requestServerSession,
        state.isLoading,
        state.isNavigating,
        sessionless,
    ]);

    useEffect(() => {
        if (state.navigateUri) {
            navigate.current(state.navigateUri, {
                replace: !state.interactiveLogin,
            });
        }
    }, [navigate, state.navigateUri, state.interactiveLogin]);

    const loading =
        tokenLoading || state.isLoading || loadingProviders || callLoginInfo;
    const showPasswordReset = settings.valueAsBool(
        "account.enablePasswordReset"
    );

    const classes = useStyles(props.customizations.styles);
    const jssSpacingStyles = useJssSpacingStyles();
    const elementBottomSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        { dir: "Bottom", size: "Large" }
    );
    const formVerticalSpacingClasses = getSpacingClasses(
        jssSpacingStyles,
        null,
        horizontalCenterPadding
    );

    const isNotLoading =
        !loading &&
        !token &&
        (state.showLoginPanel || !props.customizations.useLoginSplashScreen);
    const showSplashScreen =
        !loading &&
        !state.showLoginPanel &&
        props.customizations.useLoginSplashScreen;

    const headerContent = props.customizations.useLogoAsTitle ? (
        <Logo
            size="Medium"
            padDir="Top"
            padSize="XL"
            style={{ display: props.customizations.logoDisplay }}
            styleType={props.customizations.logoStyleType}
        />
    ) : (
        <Rubrik textCode="VisiFinance.UI.Login.Titel" rubrikSizeType="Medium" />
    );

    const isFormValid = state.username.length > 0 && state.password.length > 0;

    const detectedTenant = settings.valueAsString("detectedTenantName");
    const resetUrl =
        "/Account/Reset" + (detectedTenant ? `/${detectedTenant}` : "");

    const showLoginForm =
        forms?.toLowerCase() === "true" ||
        !state.loginInfo ||
        state.loginInfo.forms ||
        state.loginInfo.externalProviders.length == 0;

    const showExternalLogin =
        state.loginInfo?.externalProviders &&
        state.loginInfo.externalProviders.length > 0;
    const showBothExternalAndInternalLogin =
        state.loginInfo?.externalProviders &&
        state.loginInfo.externalProviders.length > 0 &&
        showLoginForm;

    return (
        <SplashPage
            isLoading={!isNotLoading}
            showSplashScreen={showSplashScreen}
            splashScreen={
                <IconButton
                    buttonClassName={classes.splashButton}
                    labelTextCode="VisiFinance.UI.Login.LoggaIn"
                    icon="Consilio.UI.Icons.Key"
                    styleType={"Transparent"}
                    onClick={() =>
                        dispatch({
                            type: "toggleLoginPanel",
                            show: !state.showLoginPanel,
                        })
                    }
                />
            }
        >
            <Form
                onSubmit={() => dispatch({ type: "tryFormLogin" })}
                isValid={isFormValid}
            >
                <SplashContent
                    header={headerContent}
                    footer={
                        showLoginForm && (
                            <Button
                                styleType={"DefaultPadded"}
                                buttonClassName={classnames(
                                    formVerticalSpacingClasses
                                )}
                                submit={true}
                                enabled={
                                    true
                                } /** When using autocomplete the username/password will not be added until the first click. We want to show that the button is still clickable. **/
                                buttonWrapperClass={classes.loginButton}
                            >
                                {intl.formatText(
                                    "VisiFinance.UI.Login.LoggaIn"
                                )}
                            </Button>
                        )
                    }
                >
                    <Grid>
                        {showExternalLogin &&
                            state.loginInfo.externalProviders.map((x) => (
                                <ExternalLoginProvider
                                    name={x}
                                    customizations={props.customizations}
                                />
                            ))}
                        {showBothExternalAndInternalLogin && (
                            <hr className={classes.loginTypeSeparator} />
                        )}
                        {showLoginForm && (
                            <>
                                <TitleText
                                    name={"Username"}
                                    autoComplete={"username"}
                                    titleTextCode="VisiFinance.UI.Login.Användarnamn"
                                    value={state.username}
                                    setValue={(x) =>
                                        dispatch({
                                            type: "updateFormCredentials",
                                            username: x,
                                            password: state.password,
                                        })
                                    }
                                    required={true}
                                />

                                <TitlePassword
                                    name={"Password"}
                                    autoComplete={"current-password"}
                                    titleTextCode="VisiFinance.UI.Login.Lösenord"
                                    value={state.password}
                                    setValue={(x) =>
                                        dispatch({
                                            type: "updateFormCredentials",
                                            username: state.username,
                                            password: x,
                                        })
                                    }
                                    required={true}
                                />

                                {state.errorMessage && (
                                    <TextBlock
                                        styleType="Paragraph"
                                        size="Small"
                                        styleSubType="Validation"
                                        text={state.errorMessage}
                                    />
                                )}

                                {showPasswordReset ? (
                                    <RouteLink
                                        className={classnames(
                                            elementBottomSpacingClasses
                                        )}
                                        to={resetUrl}
                                        textCode="VisiFinance.UI.Login.ForgotPassword"
                                    />
                                ) : (
                                    []
                                )}
                            </>
                        )}
                    </Grid>
                </SplashContent>
            </Form>

            <PoweredByConsilio />
        </SplashPage>
    );
};

//-------------------------------------------------------------------------------------
// Functions
//-------------------------------------------------------------------------------------

const tokenLoadedRedirectUrl = (
    returnUrl: string,
    serverVirtualPath: string,
    sessionless: boolean,
    status: TokenRequestStatus,
    state: LoginPageState,
    hasServerSession: ServerSessionState,
    tenantName: string
) => {
    const isSuccessfull =
        status == "account_confirmation" ||
        status == "Cleared" ||
        status == "Success" ||
        status == "SuccessAlreadyAuthenticated";

    if (status == "account_confirmation") {
        const tenantPart = tenantName ? `/${tenantName}` : "";

        return `${serverVirtualPath}Account/Confirm${tenantPart}?username=${encodeURIComponent(state.username)}`;
    }

    if (
        returnUrl &&
        isSuccessfull &&
        (sessionless ||
            hasServerSession == "ExistingSession" ||
            hasServerSession == "NewSession")
    )
        return returnUrl;

    return null;
};

const getLoginFailedMessage = (
    intl: IntlContext,
    status: string,
    mode: string
): string => {
    if (status == "invalid_grant" || (status == "Failed" && mode == "Forms")) {
        return intl.formatText(
            "VisiFinance.UI.Login.OgiltigaInloggningsuppgifter"
        );
    }

    if (status == "throttled") {
        return intl.formatText("VisiFinance.UI.Login.FörmångaAnrop");
    }

    return null;
};
